import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

export type ErrorSeverity = 'low' | 'medium' | 'high';

export interface ErrorOptions {
  severity?: ErrorSeverity;
  userMessage?: string;
  shouldReport?: boolean;
  context?: Record<string, any>;
  recovery?: () => Promise<void>;
}

export class ErrorService {
  static async handle(error: unknown, options: ErrorOptions = {}) {
    const {
      severity = 'medium',
      userMessage = 'An unexpected error occurred',
      shouldReport = true,
      context = {},
      recovery
    } = options;

    // Log to console with consistent format
    console.error('[ErrorService]', {
      error,
      severity,
      context,
      stack: error instanceof Error ? error.stack : undefined
    });

    // Show user message
    toast.error(userMessage, {
      position: 'bottom-right',
      autoClose: 5000,
    });

    // Report to Sentry if needed
    if (shouldReport && severity !== 'low') {
      Sentry.withScope((scope) => {
        scope.setLevel(severity === 'high' ? 'error' : 'warning');
        scope.setContext('error_context', context);
        scope.setTag('error_severity', severity);
        
        if (error instanceof Error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(String(error));
        }
      });
    }

    // Attempt recovery if provided
    if (recovery) {
      try {
        await recovery();
      } catch (recoveryError) {
        console.error('[ErrorService] Recovery failed:', recoveryError);
      }
    }

    return null;
  }

  static async handleAsync<T>(
    promise: Promise<T>,
    options: ErrorOptions = {}
  ): Promise<T | null> {
    try {
      return await promise;
    } catch (error) {
      return this.handle(error, options);
    }
  }
}